/**
 * Marketing-page rendering — one implementation, two consumers.
 *
 * The website service renders published pages with this, and the CMS renders its
 * live preview with the exact same function. If the preview had its own copy of
 * the markup it would drift, and a preview that lies is worse than no preview.
 *
 * Pure string in, string out: no DOM, no Node APIs, so it runs in both.
 */
const HUES = ['c-blue', 'c-teal', 'c-violet', 'c-amber'];
/**
 * The accent colours a block can wear, named rather than free hex.
 *
 * A picker that offered any colour would let an author choose one that
 * disappears against the dark panel or clashes with the status palette. These
 * four are the site's own hues, so a card always looks like it belongs — and
 * because the editor and the renderer read the same list, the swatch you click
 * is the colour you get.
 */
export const BLOCK_COLORS = [
    { id: 'c-blue', label: 'Blue', hex: '#3b82f6' },
    { id: 'c-teal', label: 'Teal', hex: '#14b8a6' },
    { id: 'c-violet', label: 'Violet', hex: '#a855f7' },
    { id: 'c-amber', label: 'Amber', hex: '#f59e0b' },
    { id: 'c-pink', label: 'Pink', hex: '#ec4899' },
    { id: 'c-green', label: 'Green', hex: '#22c55e' },
];
const COLOR_IDS = new Set(BLOCK_COLORS.map((c) => c.id));
export function escapeHtml(value) {
    return String(value ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);
}
/**
 * Allow-list sanitiser for rich-text fields.
 *
 * This HTML is authored in the admin app but *served on the public website*, so
 * it is sanitised on the way in rather than trusted. Only formatting tags
 * survive; scripts, event handlers, styles and javascript: URLs do not.
 */
const ALLOWED_TAGS = new Set([
    'p', 'br', 'strong', 'b', 'em', 'i', 'u', 'ul', 'ol', 'li',
    'h2', 'h3', 'h4', 'blockquote', 'a', 'code', 'pre',
]);
/**
 * Tags that are safe but not in the vocabulary we render — rewritten instead of
 * dropped. Browsers wrap each line of a contenteditable in a <div> when you
 * press Enter; stripping those ran every paragraph together, which looked like
 * the editor was ignoring the return key.
 */
const REWRITE_TAGS = { div: 'p', span: '', section: 'p', h1: 'h2' };
export function sanitizeHtml(input) {
    if (!input)
        return '';
    let out = input;
    // Drop whole elements whose content is never safe to keep.
    out = out.replace(/<(script|style|iframe|object|embed|form)[\s\S]*?<\/\1>/gi, '');
    out = out.replace(/<(script|style|iframe|object|embed|form)[^>]*\/?>/gi, '');
    out = out.replace(/<\/?([a-zA-Z0-9]+)([^>]*)>/g, (match, rawTag, attrs) => {
        let tag = rawTag.toLowerCase();
        if (!ALLOWED_TAGS.has(tag)) {
            const rewritten = REWRITE_TAGS[tag];
            // `span` maps to nothing: drop the tag, keep the text inside it.
            if (rewritten === undefined)
                return '';
            if (rewritten === '')
                return '';
            tag = rewritten;
        }
        if (match.startsWith('</'))
            return `</${tag}>`;
        // Links may keep a safe href; everything else loses its attributes, which
        // removes on* handlers and style-based injection in one go.
        if (tag === 'a') {
            const href = /href\s*=\s*("([^"]*)"|'([^']*)')/i.exec(attrs);
            const url = (href?.[2] ?? href?.[3] ?? '').trim();
            const safe = /^(https?:|mailto:|tel:|\/|#)/i.test(url);
            return safe
                ? `<a href="${escapeHtml(url)}" rel="noopener noreferrer">`
                : '<a>';
        }
        return `<${tag}>`;
    });
    return out;
}
/**
 * Escape plain text, but keep the author's line breaks.
 *
 * Plain-text fields arrive with real newlines from a textarea, and HTML
 * collapses those to a single space — so pressing Enter in any non-rich field
 * silently did nothing on the published page. Escaping happens first, so the
 * <br> we add is the only markup that survives.
 */
export function escapeWithBreaks(value) {
    return escapeHtml(value).replace(/\r?\n/g, '<br>');
}
/**
 * The live inspection viewport shown beside the landing hero — a scanning line, a
 * defect box and a verdict that cycles GOOD/BAD/UNSURE. Decorative and static
 * (aria-hidden), so it carries no editable fields; the animation is pure CSS in
 * style.css. It does on the page what the product does on the line.
 */
const HERO_SCOPE = `<div class="hero-scope"><div class="scope" aria-hidden="true">
  <div class="scope-grid"></div>
  <svg class="scope-part" viewBox="0 0 100 100" fill="none">
    <defs><radialGradient id="metal" cx="42%" cy="34%" r="70%">
      <stop offset="0%" stop-color="#3a4757" /><stop offset="60%" stop-color="#232c37" /><stop offset="100%" stop-color="#161c24" />
    </radialGradient></defs>
    <circle cx="50" cy="50" r="46" fill="url(#metal)" stroke="#3a4757" stroke-width="0.6" />
    <circle cx="50" cy="50" r="34" fill="none" stroke="#4a5a6d" stroke-width="1.2" />
    <circle cx="50" cy="50" r="18" fill="#10151c" stroke="#4a5a6d" stroke-width="1.2" />
    <g fill="#2a333f" stroke="#54657a" stroke-width="0.5">
      <circle cx="76" cy="50" r="3.4" /><circle cx="72.5" cy="63" r="3.4" /><circle cx="63" cy="72.5" r="3.4" /><circle cx="50" cy="76" r="3.4" />
      <circle cx="37" cy="72.5" r="3.4" /><circle cx="27.5" cy="63" r="3.4" /><circle cx="24" cy="50" r="3.4" /><circle cx="27.5" cy="37" r="3.4" />
      <circle cx="37" cy="27.5" r="3.4" /><circle cx="50" cy="24" r="3.4" /><circle cx="63" cy="27.5" r="3.4" /><circle cx="72.5" cy="37" r="3.4" />
    </g>
    <path d="M64 40 q6 4 3 12" stroke="#f59e0b" stroke-width="1.6" fill="none" opacity="0.9" />
  </svg>
  <div class="scope-box"><span>defect · 0.94</span></div>
  <div class="scope-scan"></div>
  <div class="scope-corner tl"></div><div class="scope-corner tr"></div><div class="scope-corner bl"></div><div class="scope-corner br"></div>
  <div class="scope-verdict"><span class="chip good">✓ GOOD</span><span class="chip bad">✕ BAD</span><span class="chip uns">? UNSURE</span></div>
  <div class="scope-readout">score <b>0.98</b> · <b>42</b> ms · model v7</div>
</div></div>`;
/**
 * Two live demo scenes for the landing page, under the showcase — the line and
 * the safety floor, a taste of what the /in-action page shows in full. Pure
 * CSS/SVG (styles in style.css), decorative and aria-hidden.
 */
const HOME_SCENES = `<section>
  <div class="page-head" style="padding:0"><span class="eyebrow">See it work</span>
    <h2 class="block-title">Not a slideshow — the real thing, playing out</h2></div>
  <div class="scene-block c-blue" style="margin-top:20px">
    <div class="scene-text">
      <span class="step-tag">On the line</span>
      <h2>Every part, scored as it passes</h2>
      <p>A camera over the belt, a verdict the instant each part is under it — GOOD, BAD, or UNSURE when the call is too close to guess.</p>
    </div>
    <div class="scene-stage line-stage" aria-hidden="true">
      <div class="line-grid"></div>
      <div class="line-cam"><span class="line-cam-scan"></span></div>
      <div class="line-verdict"><span class="chip good">✓ GOOD</span><span class="chip bad">✕ BAD</span></div>
      <div class="belt"><span class="belt-part p1"></span><span class="belt-part p2 bad"></span><span class="belt-part p3"></span><span class="belt-part p4"></span></div>
      <div class="belt-rail"></div>
    </div>
  </div>
  <div class="scene-block reverse c-green" style="margin-top:20px">
    <div class="scene-text">
      <span class="step-tag">On the floor</span>
      <h2>Watch the floor, not just the part</h2>
      <p>The same eye that grades a part watches the workplace — PPE, a forklift in someone’s path, smoke where there shouldn’t be. Events the moment they happen.</p>
    </div>
    <div class="scene-stage safety-stage" aria-hidden="true">
      <div class="line-grid"></div>
      <div class="zone"></div><span class="zone-tag">NO-ENTRY</span>
      <div class="worker">
        <svg viewBox="0 0 40 90" fill="#8aa0b6"><circle cx="20" cy="12" r="9"/><rect x="9" y="22" width="22" height="34" rx="6"/><rect x="12" y="55" width="7" height="30" rx="3"/><rect x="21" y="55" width="7" height="30" rx="3"/></svg>
        <span class="worker-box"></span><span class="worker-label">person · vest ✓ · helmet ✓</span>
      </div>
      <div class="forklift">
        <svg viewBox="0 0 90 60" fill="none" stroke="#f59e0b" stroke-width="3"><rect x="30" y="16" width="30" height="24" rx="3"/><path d="M30 40h34"/><path d="M60 22h14v18"/><path d="M74 14v34"/><path d="M74 16h10M74 26h10"/><circle cx="38" cy="50" r="7"/><circle cx="60" cy="50" r="7"/></svg>
        <span class="forklift-box"></span>
      </div>
      <span class="nearmiss"></span><div class="safety-alert"><span class="chip bad">▲ NEAR MISS</span></div>
      <span class="line-cam-scan floor-scan"></span>
    </div>
  </div>
  <p style="text-align:center;margin-top:22px"><a class="btn lg" href="/in-action">See all three, in action →</a></p>
</section>`;
/** Render one content block. Shared by the site and the CMS preview. */
export function renderBlock(block, index) {
    const esc = escapeHtml;
    // An author's choice wins over the positional default. Without this a block's
    // accent changed the moment it was moved up or down the page, which reads as
    // the editor losing your settings.
    const hue = COLOR_IDS.has(String(block.color))
        ? String(block.color)
        : HUES[index % HUES.length];
    const items = Array.isArray(block.items) ? block.items : [];
    const img = (src, alt) => src
        ? `<img src="${esc(src)}" alt="${esc(alt)}" loading="lazy" />`
        : '<div class="img-placeholder">No image yet</div>';
    switch (block.type) {
        case 'heading':
            return `<section class="page-head">
          ${block.eyebrow ? `<span class="eyebrow">${esc(block.eyebrow)}</span>` : ''}
          ${block.title ? `<h2 class="block-title">${esc(block.title)}</h2>` : ''}
          ${block.lead ? `<p class="lead">${escapeWithBreaks(block.lead)}</p>` : ''}
        </section>`;
        case 'text':
            return `<section class="doc narrow">${sanitizeHtml(String(block.body ?? ''))}</section>`;
        case 'image':
            return `<figure class="block-image ${hue}">
          ${img(block.image, block.caption)}
          ${block.caption ? `<figcaption>${esc(block.caption)}</figcaption>` : ''}
        </figure>`;
        case 'imageText':
            return `<section class="step-block ${hue}${String(block.side).toLowerCase() === 'left' ? '' : ' reverse'}">
          <div class="step-text">
            ${block.title ? `<h2>${esc(block.title)}</h2>` : ''}
            ${block.body ? `<p>${escapeWithBreaks(block.body)}</p>` : ''}
          </div>
          <figure>${img(block.image, block.title)}</figure>
        </section>`;
        case 'features':
            return `<section class="features">${items
                .map((it, i) => `<article class="${HUES[i % HUES.length]}"><h3>${esc(it.title)}</h3><p>${escapeWithBreaks(it.body)}</p></article>`)
                .join('')}</section>`;
        case 'team':
            return `<section class="team-grid">${items
                .map((it, i) => `<article class="${HUES[i % HUES.length]}">
               <div class="team-photo">${img(it.image, it.title)}</div>
               <h3>${esc(it.title)}</h3>
               ${it.role ? `<p class="team-role">${esc(it.role)}</p>` : ''}
               ${it.body ? `<p>${escapeWithBreaks(it.body)}</p>` : ''}
             </article>`)
                .join('')}</section>`;
        case 'stats':
            return `<div class="stats-strip">${items
                .map((it, i) => `<div class="${HUES[i % HUES.length]}"><strong>${esc(it.value)}</strong><span>${esc(it.label)}</span></div>`)
                .join('')}</div>`;
        case 'cta':
            return `<section class="cta-band">
          ${block.title ? `<h2>${esc(block.title)}</h2>` : ''}
          ${block.body ? `<p class="lead">${escapeWithBreaks(block.body)}</p>` : ''}
          ${block.buttonText
                ? `<a class="btn primary lg" href="${esc(block.buttonHref || '/contact')}">${esc(block.buttonText)}</a>`
                : ''}
        </section>`;
        default:
            return '';
    }
}
/** The extra blocks a page carries, whichever template it uses. */
function blocksOf(content) {
    return Array.isArray(content.blocks) ? content.blocks : [];
}
/**
 * Wrap a block in a panel with a coloured left border, if the author asked for
 * one.
 *
 * The colour is only ever a class from BLOCK_COLORS — never an inline style
 * built from stored content. Page content is author-supplied, and letting it
 * reach a `style` attribute is how a CMS grows an injection hole.
 */
function asCard(html, block) {
    if (!html || block.card !== true)
        return html;
    const color = COLOR_IDS.has(String(block.color)) ? String(block.color) : 'c-blue';
    return `<div class="block-card ${color}">${html}</div>`;
}
/**
 * Lay the blocks out in order, gathering runs of half-width blocks into a
 * two-column grid.
 *
 * Grouping consecutive blocks rather than making each one its own column is
 * what lets an author mix widths freely: six half blocks become three tidy
 * rows, and a full-width block in the middle simply breaks the grid and starts
 * a new one after it. Blocks without a width keep the old full-width
 * behaviour, so existing pages render exactly as before.
 */
function renderBlockFlow(blocks) {
    const out = [];
    let column = [];
    const flush = () => {
        if (column.length === 0)
            return;
        // A lone half block in a two-column grid would just be a narrow orphan.
        out.push(column.length === 1
            ? column[0]
            : `<div class="block-columns">${column.join('\n')}</div>`);
        column = [];
    };
    blocks.forEach((b, i) => {
        const html = asCard(renderBlock(b, i), b);
        if (!html)
            return;
        if (b.width === 'half')
            column.push(html);
        else {
            flush();
            out.push(html);
        }
    });
    flush();
    return out.join('\n');
}
/** Render a page's body markup for its template. */
/** A colourful pricing table from the landing template's `pricing` items. */
function renderPricing(c) {
    const plans = Array.isArray(c.pricing) ? c.pricing : [];
    if (!plans.length)
        return '';
    const esc = escapeHtml;
    // Plain-text features, with a light **bold** → <b> for the headline number.
    const bold = (s) => esc(s).replace(/\*\*(.+?)\*\*/g, '<b>$1</b>');
    const cards = plans
        .map((p) => {
        const hue = COLOR_IDS.has(String(p.color)) ? String(p.color) : 'blue';
        const featured = String(p.featured).toLowerCase() === 'yes';
        const feats = String(p.features ?? '')
            .split(/\r?\n/)
            .filter((f) => f.trim())
            .map((f) => `<li>${bold(f)}</li>`)
            .join('');
        return `<article class="plan c-${hue}${featured ? ' featured' : ''}">
          ${featured ? '<span class="plan-badge">Most popular</span>' : ''}
          <h3>${esc(p.name)}</h3>
          <div class="price">${esc(p.price)}${p.period ? `<span>${esc(p.period)}</span>` : ''}</div>
          ${p.tag ? `<p class="plan-tag">${esc(p.tag)}</p>` : ''}
          <ul>${feats}</ul>
          ${p.buttonText ? `<a class="btn ${featured ? 'primary ' : ''}lg" href="/contact">${esc(p.buttonText)}</a>` : ''}
        </article>`;
    })
        .join('');
    return `<section id="pricing">
      <div class="page-head" style="padding:0;text-align:center">
        ${c.pricingEyebrow ? `<span class="eyebrow">${esc(c.pricingEyebrow)}</span>` : ''}
        ${c.pricingTitle ? `<h2 class="block-title">${esc(c.pricingTitle)}</h2>` : ''}
        ${c.pricingLead ? `<p class="lead" style="max-width:640px;margin:10px auto 0">${escapeWithBreaks(c.pricingLead)}</p>` : ''}
      </div>
      <div class="pricing">${cards}</div>
    </section>`;
}
/** A "real detections" image gallery from the landing template's `examples` items. */
function renderExamples(c) {
    const items = Array.isArray(c.examples) ? c.examples : [];
    if (!items.length)
        return '';
    const esc = escapeHtml;
    const figs = items
        .map((e) => `<figure>${e.image ? `<img src="${esc(e.image)}" alt="${esc(e.title)}" loading="lazy" />` : ''}<figcaption><strong>${esc(e.title)}</strong>${escapeWithBreaks(e.body)}</figcaption></figure>`)
        .join('');
    return `<section>
      <div class="page-head narrow" style="padding-bottom:0">
        ${c.examplesEyebrow ? `<span class="eyebrow">${esc(c.examplesEyebrow)}</span>` : ''}
        ${c.examplesTitle ? `<h2>${esc(c.examplesTitle)}</h2>` : ''}
        ${c.examplesLead ? `<p class="lead">${escapeWithBreaks(c.examplesLead)}</p>` : ''}
      </div>
      <div class="example-grid">${figs}</div>
    </section>`;
}
export function renderPageContent(page) {
    const c = page.content ?? {};
    const items = Array.isArray(c.items) ? c.items : [];
    const esc = escapeHtml;
    const head = `
    <section class="page-head${page.template === 'text' ? ' narrow' : ''}">
      ${c.eyebrow ? `<span class="eyebrow">${esc(c.eyebrow)}</span>` : ''}
      <h1>${esc(c.title || page.title)}</h1>
      ${c.lead ? `<p class="lead">${escapeWithBreaks(c.lead)}</p>` : ''}
    </section>`;
    // A flexible page is simply its blocks, in the order they were arranged.
    if (page.template === 'blocks') {
        const blocks = Array.isArray(c.blocks) ? c.blocks : [];
        if (blocks.length === 0) {
            return `${head}<section class="doc narrow"><p>This page has no content blocks yet.</p></section>`;
        }
        return `${head}${renderBlockFlow(blocks)}`;
    }
    if (page.template === 'landing') {
        const stats = Array.isArray(c.stats) ? c.stats : [];
        const showcase = Array.isArray(c.showcase) ? c.showcase : [];
        return `
      <section class="hero hero-split">
        <div class="hero-glow" aria-hidden="true"></div>
        <div class="hero-copy">
          ${c.eyebrow ? `<span class="eyebrow"><span class="live-dot" aria-hidden="true"></span> ${esc(c.eyebrow)}</span>` : ''}
          <h1>${esc(c.title || page.title)}</h1>
          ${c.lead ? `<p class="lead">${escapeWithBreaks(c.lead)}</p>` : ''}
          <div class="cta">
            ${c.ctaPrimary ? `<a class="btn primary lg" href="/contact">${esc(c.ctaPrimary)}</a>` : ''}
            ${c.ctaSecondary ? `<a class="btn ghost lg" href="/how-it-works">${esc(c.ctaSecondary)}</a>` : ''}
          </div>
        </div>
        ${HERO_SCOPE}
      </section>
      ${stats.length
            ? `<div class="stats-strip">${stats
                .map((s2, i) => `<div class="${HUES[i % HUES.length]}"><strong>${esc(s2.value)}</strong><span>${esc(s2.label)}</span></div>`)
                .join('')}</div>`
            : ''}
      ${showcase.length
            ? `<section class="showcase">${showcase
                .map((s2, i) => `<figure class="${HUES[i % HUES.length]}">
                     <div class="showcase-demo">
                       ${s2.image ? `<img src="${esc(s2.image)}" alt="${esc(s2.title)}" loading="lazy" />` : '<div class="img-placeholder">No image yet</div>'}
                       <span class="demo-scan" aria-hidden="true"></span>
                       <span class="demo-box" aria-hidden="true"></span>
                       <span class="demo-corner tl" aria-hidden="true"></span><span class="demo-corner br" aria-hidden="true"></span>
                     </div>
                     <figcaption><strong>${esc(s2.title)}</strong>${esc(s2.body)}</figcaption>
                   </figure>`)
                .join('')}</section>`
            : ''}
      ${c.showScenes === false ? '' : HOME_SCENES}
      ${items.length
            ? `<section class="features">${items
                .map((it, i) => `<article class="${HUES[i % HUES.length]}"><h3>${esc(it.title)}</h3><p>${escapeWithBreaks(it.body)}</p></article>`)
                .join('')}</section>`
            : ''}
      ${renderPricing(c)}
      ${renderExamples(c)}
      ${c.safetyTitle
            ? `<section class="safety"><div class="safety-icon">🛡</div><div>
               <h2>${esc(c.safetyTitle)}</h2><p>${escapeWithBreaks(c.safetyBody)}</p></div></section>`
            : ''}
      ${renderBlockFlow(blocksOf(c))}
      ${
        // Absent means shown: pages written before this option keep their form.
        c.showEnquiry === false
            ? ''
            : `<section class="enquiry"><div><h2>${esc(c.enquiryTitle || 'See it on your product')}</h2>
        <p class="lead">${escapeWithBreaks(c.enquiryBody || "Tell us what you make and we'll show you what it would catch.")}</p></div>{{form}}</section>`}`;
    }
    if (page.template === 'features') {
        return `${head}<section class="features">${items
            .map((it, i) => `<article class="${HUES[i % HUES.length]}">
             <h3>${esc(it.title)}</h3><p>${escapeWithBreaks(it.body)}</p>
           </article>`)
            .join('')}</section>${renderBlockFlow(blocksOf(c))}`;
    }
    if (page.template === 'steps') {
        return `${head}${items
            .map((it, i) => `<section class="step-block ${HUES[i % HUES.length]}${i % 2 ? ' reverse' : ''}">
             <div class="step-text">
               <span class="step-tag">Step ${i + 1}</span>
               <h2>${esc(it.title)}</h2><p>${escapeWithBreaks(it.body)}</p>
             </div>
             <figure>${it.image
            ? `<img src="${esc(it.image)}" alt="${esc(it.title)}" loading="lazy" />`
            : '<div class="img-placeholder">No image yet</div>'}</figure>
           </section>`)
            .join('')}${renderBlockFlow(blocksOf(c))}`;
    }
    if (page.template === 'contact') {
        return `${head}
      <section class="contact-grid">
        <div class="contact-details">
          <div class="contact-card c-blue"><h3>Company</h3><p><strong>BCKSPCE HOLDING SARL</strong><br />247 Impasse des Guinettes<br />74210 Doussard<br />Haute-Savoie, France</p></div>
          <div class="contact-card c-teal"><h3>Email</h3><p><a href="mailto:thevisionqc@bckspce.com">thevisionqc@bckspce.com</a></p></div>
          <div class="contact-card c-violet"><h3>Phone</h3><p><a href="tel:+33608468630">+33 6 08 46 86 30</a></p></div>
        </div>
        <div>${c.body ? `<p class="lead">${escapeWithBreaks(c.body)}</p>` : ''}{{form}}</div>
      </section>
      ${renderBlockFlow(blocksOf(c))}`;
    }
    return `${head}<section class="doc narrow">${sanitizeHtml(String(c.body ?? ''))}</section>${renderBlockFlow(blocksOf(c))}`;
}
//# sourceMappingURL=renderSite.js.map